var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { AsciiText } from './AsciiText';
import { CommandBuffer } from './CommandBuffer';
import { CommandFormatter } from './CommandFormatter';
import { isSocketEmulatorConstructor, } from './Emulator';
import { emulators } from './Emulators';
import { Timeouts } from './Timeouts';
import { isPlainAsyncHandler, isRegExpAsyncHandler } from './Transport';
const logReceivedCommands = false;
const logWrites = false;
let id = 0;
export class SocketEmulator {
    constructor(key, logFactory, commandExtractor, sendTerminator, isBinary = false) {
        var _a, _b;
        this.isBinary = isBinary;
        this.canConnect = true;
        this.isProcessingData = false;
        this._negotiateTelnet = false;
        this._state = 'disconnected';
        this.timeouts = new Timeouts();
        this._key = `emulator.${(id++).toString()}`;
        const [name, Emulator] = this.getEmulator(key);
        this.log = logFactory.channel('SocketEmulator');
        if (isSocketEmulatorConstructor(Emulator)) {
            this._emulator = new Emulator({
                getHost: () => this._host,
                getPort: () => this._port,
                onBeforeConnect: this.onBeforeConnect.bind(this),
                send: this.write.bind(this),
                sendRaw: this.writeRaw.bind(this),
                setCanConnect: this.setCanConnect.bind(this),
            }, logFactory.channel(name));
        }
        else {
            throw new Error('unsupported emulator type');
        }
        if (this._emulator.parser !== undefined) {
            this.parser = this._emulator.parser.bind(this._emulator);
        }
        this.sendTerminator = (_a = this._emulator.sendTerminator) !== null && _a !== void 0 ? _a : sendTerminator;
        this.commandBuffer = new CommandBuffer((_b = this._emulator.commandExtractor) !== null && _b !== void 0 ? _b : commandExtractor);
    }
    // getters and setters
    get emulator() {
        return this._emulator;
    }
    get host() {
        return this._host;
    }
    get key() {
        return this._key;
    }
    get negotiateTelnet() {
        return this._negotiateTelnet;
    }
    set negotiateTelnet(value) {
        this._negotiateTelnet = value;
    }
    get port() {
        return this._port;
    }
    get state() {
        return this._state;
    }
    // public methods
    connect(host, port, username, // specify username and password for ssh
    password) {
        return __awaiter(this, void 0, void 0, function* () {
            if (this.clientOnBeforeConnectCallback) {
                yield this.clientOnBeforeConnectCallback(host, port, username, password);
            }
            this._host = host;
            this._port = port;
            this._state = 'connecting';
            this.log.info(this._state);
            this.commandBuffer.clear(); // needed for devices that reconnect with the same socket
            yield this.timeouts.delay(1000);
            if (this.canConnect) {
                this._state = 'connected';
                this.log.info(this._state);
            }
            else {
                this._state = 'disconnected';
                this.log.info(this._state);
                return Promise.reject(new Error('error opening socket: connection refused'));
            }
        });
    }
    disconnect() {
        return __awaiter(this, void 0, void 0, function* () {
            this._state = 'disconnecting';
            this.log.info(this._state);
            yield this.timeouts.delay(1000);
            this.log.info('closed');
            this._state = 'disconnected';
            this.timeouts.clearAll();
            if (this.clientConnectionLostCallback) {
                this.clientConnectionLostCallback();
            }
        });
    }
    onConnectionLost(connectionLostCallback) {
        this.clientConnectionLostCallback = connectionLostCallback;
        return this;
    }
    onData(dataCallback) {
        this.clientDataCallback = dataCallback;
        return this;
    }
    send(data) {
        return __awaiter(this, void 0, void 0, function* () {
            this.commandBuffer.add(AsciiText.decode(data));
            this.timeouts.set(() => __awaiter(this, void 0, void 0, function* () { return this.processReceivedData(); }));
        });
    }
    // private methods
    getEmulator(key) {
        for (const [name, Emulator] of Object.entries(emulators)) {
            if (Emulator.key === key)
                return [name, Emulator];
        }
        throw new Error(`no emulator found for key: ${key}`);
    }
    onBeforeConnect(beforeConnectCallback) {
        this.clientOnBeforeConnectCallback = beforeConnectCallback;
    }
    processReceivedData() {
        return __awaiter(this, void 0, void 0, function* () {
            if (this.state !== 'connected' || this.isProcessingData)
                return;
            const command = this.commandBuffer.getCommand();
            if (command === undefined) {
                this.isProcessingData = false;
                return;
            }
            if (logReceivedCommands) {
                const cmd = CommandFormatter.format(command, this.isBinary);
                this.log.debug(`emulator: received: ${cmd}`);
            }
            this.isProcessingData = true;
            let handled = false;
            let parsedCommand;
            for (const handler of this._emulator.asyncHandlers) {
                if (isRegExpAsyncHandler(handler)) {
                    const response = handler.regExp.exec(command);
                    if (response) {
                        handler.callback(response);
                        handled = true;
                        break;
                    }
                }
                else if (isPlainAsyncHandler(handler)) {
                    if (this.parser) {
                        if (parsedCommand === undefined) {
                            parsedCommand = this.parser(command);
                        }
                        if (parsedCommand !== undefined) {
                            const response = handler.match(parsedCommand);
                            if (response !== undefined) {
                                handler.callback(response);
                                handled = true;
                                break;
                            }
                        }
                    }
                    else {
                        const response = handler.match(command);
                        if (response !== undefined) {
                            handler.callback(response);
                            handled = true;
                            break;
                        }
                    }
                }
            }
            if (!handled) {
                const cmd = CommandFormatter.format(command, this.isBinary);
                this.log.error(`command not handled by emulator: ${cmd}`);
            }
            this.isProcessingData = false;
            this.timeouts.set(() => __awaiter(this, void 0, void 0, function* () { return this.processReceivedData(); }));
        });
    }
    setCanConnect(canConnect) {
        if (this.canConnect === canConnect)
            return;
        this.canConnect = canConnect;
        if (!this.canConnect && this.state !== 'disconnected') {
            this._state = 'disconnected';
            this.log.info(this._state);
            if (this.clientConnectionLostCallback)
                this.clientConnectionLostCallback();
        }
    }
    write(data) {
        this.writeRaw(data + this.sendTerminator);
    }
    writeRaw(data) {
        if (logWrites) {
            const cmd = CommandFormatter.format(data, this.isBinary);
            this.log.debug(`emulator: writing: ${cmd}`);
        }
        if (this.clientDataCallback) {
            this.clientDataCallback(AsciiText.encode(data));
        }
    }
}
